import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { IAlertAction } from "@/api_framework/api_modals/Configuration";
import { AttendanceSection, useAlertActionAPI } from "@/app/hooks/api_hooks/configuration/useAlertActionAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

export const ALERT_LABELS: Record<string, string> = {
  SHORT_SHIFT: "Short Shift",
  ROTA_NOT_PRESENT: "ROTA not present",
  STAFF_ABSENT: "Staff Absent",
  NO_OUT_TIME: "No out time",
  SHORT_STAFF: "Short Staff",
  MORE_STAFF: "More Staff",
  EXTRA_STAFF: "Extra staff identified",
};

export const REASON_LABELS: Record<string, Record<string, string>> = {
  SHORT_SHIFT: {
    ATTENDANCE_TIME_CORRECT: "Staff worked full shift, punch is wrong.",
    CAME_LATE: "Staff came late but stayed till shift end. No action needed",
    LEFT_EARLY: "Staff left early.",
    MOVED_TO_OTHER_CLINIC: "Staff moved to another clinic during the day.",
    OTHER: "Other (write at least 25 characters)",
  },
  ROTA_NOT_PRESENT: {
    MISSED_BY_CENTRAL_TEAM: "The Central team missed the rota. Central team will add it",
    CAME_ON_WEEK_OFF: "The staff came on a week off.",
    MOVED_FROM_OTHER_CLINIC: "Staff moved here from another clinic.",
    NEW_JOINEE: "New joinee, rota not made yet.",
    OTHER: "Other (write at least 25 characters)",
  },
  STAFF_ABSENT: {
    STAFF_WAS_PRESENT: "Staff worked, attendance not marked.",
    DID_NOT_COME: "Staff did not come (leave, week off or no information).",
    RESIGNED: "Staff has resigned",
    MOVED_TO_OTHER_CLINIC: "Staff moved to another clinic.",
    OTHER: "Other (write at least 25 characters)",
  },
  SHORT_STAFF: {
    MISSED_REMOVAL_BY_AREA_MANAGER: "Missed to be removed by Area Manager",
    AS_PER_CLINIC_CONFIGURATION: "As per Clinic Configuration",
    STAFF_UNWELL: "Staff was not well",
    MOVED_TO_OTHER_CLINIC: "Staff moved to other clinic",
    OTHER: "Other",
  },
  MORE_STAFF: {
    MISSED_BY_CENTRAL_TEAM: "Missed by Central team",
    CAME_ON_WEEK_OFF: "Staff came on week off day",
    MOVED_FROM_OTHER_CLINIC: "Staff moved from other clinic",
    OTHER: "Other",
  },
};

const MARK_AR_NOTE = "Kindly ask staff to mark AR on HROne, or raise a ticket on HROne.";
const ADD_ROTA_NOTE = "Kindly ask Central Team to add Rota";

const REASON_NOTES: Record<string, Record<string, string>> = {
  STAFF_ABSENT: {
    STAFF_WAS_PRESENT: MARK_AR_NOTE,
    RESIGNED: "Ask the HR to check and exit the Emp from System",
  },
  SHORT_SHIFT: {
    ATTENDANCE_TIME_CORRECT: MARK_AR_NOTE,
    CAME_LATE: MARK_AR_NOTE,
    MOVED_TO_OTHER_CLINIC: MARK_AR_NOTE,
  },
  ROTA_NOT_PRESENT: {
    MISSED_BY_CENTRAL_TEAM: ADD_ROTA_NOTE,
    NEW_JOINEE: ADD_ROTA_NOTE,
  },
};

export const GROOMER_REASON_LABELS: Record<string, Record<string, string>> = REASON_LABELS;

export const ALL_STAFF_REASON_LABELS: Record<string, Record<string, string>> = {
  ...REASON_LABELS,
  EXTRA_STAFF: {
    NEW_JOINEE: "New Joinee",
    MOVED_FROM_OTHER_CLINIC: "Moved from other clinic",
    OTHER: "Other",
  },
};

const REASONS_BY_SECTION: Record<AttendanceSection, Record<string, Record<string, string>>> = {
  doctors: REASON_LABELS,
  groomers: GROOMER_REASON_LABELS,
  all_staff: ALL_STAFF_REASON_LABELS,
};

const MIN_NOTE_LENGTH = 25;

interface IAlertSubject {
  staff_id?: string;
  name: string;
  alert: string | null;
  action: IAlertAction | null;
}

interface AlertActionModalProps {
  row: IAlertSubject | null;
  section?: AttendanceSection;
  clinicId: string;
  date: string;
  onClose: () => void;
  onSaved: () => void;
}

const AlertActionModal = ({ row, section = "doctors", clinicId, date, onClose, onSaved }: AlertActionModalProps) => {
  const { execute, fieldErrors, isSaving } = useAlertActionAPI();
  const { execute: logAPI } = useClickLogAPI();
  const mappedClinicName = useAppSelector((state) => state.root.UIStates.mappedClinicName);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");

  const alertType = row?.alert || "";

  const logAttendanceEvent = (eventType: string, extra: Partial<IClickLogBody> = {}) => {
    logAPI({
      event_type: eventType,
      scr_id: "day_closing_staff_attendance",
      timestamp: Date.now(),
      vms_user_id: userLogInfo,
      clinic_id: clinicId,
      clinic_name: mappedClinicName,
      staff_id: row?.staff_id,
      attendance_date: date,
      attendance_section: section,
      alert_type: alertType,
      ...extra,
    });
  };

  useEffect(() => {
    setReasonCode(row?.action?.reason_code || "");
    setNote(row?.action?.note || "");
    if (row && !row.action) logAttendanceEvent("STAFF_ATTENDANCE_RAISE_REQUEST_CLICKED");
  }, [row?.staff_id, row?.name, row?.alert, row?.action]);

  const reasons = REASONS_BY_SECTION[section][alertType] || {};
  const actionNote = REASON_NOTES[alertType]?.[reasonCode] || "";
  const needsNote = reasonCode === "OTHER";
  const noteLength = note.trim().length;
  const canSave = Boolean(reasonCode) && (!needsNote || noteLength >= MIN_NOTE_LENGTH);

  const onSave = async () => {
    if (!row || !canSave) return;
    const saved = await execute(clinicId, {
      date,
      section,
      ...(row.staff_id ? { staff_id: row.staff_id } : {}),
      alert_type: alertType,
      reason_code: reasonCode,
      reason_label: reasons[reasonCode],
      note: needsNote ? note.trim() : "",
      action_note: actionNote,
    });
    logAttendanceEvent("STAFF_ATTENDANCE_REQUEST_SUBMITTED", {
      event_action: saved ? "SUCCESS" : "FAILED",
      reason_code: reasonCode,
    });
    if (saved) {
      onSaved();
      onClose();
    }
  };

  return (
    <Transition.Root show={Boolean(row)} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-lg overflow-visible rounded-xl bg-white shadow-xl">
              <div className="border-b border-slate-100 px-5 py-4">
                <Dialog.Title className="text-sm font-semibold text-slate-900">
                  {ALERT_LABELS[alertType] || alertType} — {row?.name}
                </Dialog.Title>
                <p className="mt-0.5 text-xs text-[#6C727F]">Recorded in VMS only. Nothing is sent to HROne.</p>
              </div>

              <div className="px-5 py-4">
                <fieldset>
                  <legend className="text-xs font-medium text-[#1F1F23]">Reason</legend>
                  <div className="mt-2 flex flex-col gap-1">
                    {Object.entries(reasons).map(([code, label]) => (
                      <Fragment key={code}>
                        <label className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-2 text-xs text-slate-700 transition-colors hover:bg-slate-50">
                          <input
                            type="radio"
                            name="reason"
                            value={code}
                            checked={reasonCode === code}
                            onChange={() => setReasonCode(code)}
                            className="mt-0.5 h-3.5 w-3.5 accent-[#193A6A]"
                          />
                          {label}
                        </label>
                        {reasonCode === code && actionNote && (
                          <p className="ml-7 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] leading-4 text-amber-800">
                            {actionNote}
                          </p>
                        )}
                      </Fragment>
                    ))}
                  </div>
                </fieldset>

                {needsNote && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-[#1F1F23]">Describe the reason</label>
                    <textarea
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      rows={3}
                      placeholder="What happened, and why is this acceptable?"
                      className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-slate-700 placeholder:text-gray-400 transition-colors focus:border-[#193A6A] focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15"
                    />
                    <p
                      className={`mt-1 text-[11px] leading-4 ${
                        noteLength >= MIN_NOTE_LENGTH ? "text-[#6C727F]" : "text-rose-500"
                      }`}
                    >
                      {noteLength}/{MIN_NOTE_LENGTH} characters
                    </p>
                  </div>
                )}

                {fieldErrors.map((error) => (
                  <p key={error.field} className="mt-2 text-[11px] leading-4 text-rose-500">
                    {error.message}
                  </p>
                ))}

                {row?.action && (
                  <p className="mt-3 text-[11px] leading-4 text-[#6C727F]">
                    Recorded by {row.action.created_by}. This alert cannot be actioned again.
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-5 py-4">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <SolidButton size="2XL" disabled={!canSave || isSaving} onButtonClick={onSave}>
                  {isSaving ? "Saving" : "Submit request"}
                </SolidButton>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AlertActionModal;
